
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CompanyT } from '@/core/types/companies.types'
import { CityT } from '@/core/types/cities.types'
import { AdditionalServicesTypeT } from '@/core/types/additional_services.types'

// realisations
import { companies } from '@/core/realisations/companies.realisation'
import { cities } from '@/core/realisations/cities.realisation'
import { additionalServices } from '@/core/realisations/additional_services.realisation'

// constants
import {
    WITH_SERVICES_STATUSES,
    WITH_SERVICES_STATUSES_ADDITIONAL,
} from '@/core/constants/with_services.constants'

export default defineComponent({
    components: {
        TheInput,
        TheSelect,
        Checkbox,
        TheButton,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['apply', 'reset'],
    setup(props, { emit }) {
        const state = reactive({
            invoice_number: null as string | null,
            order_id: null as string | null,
            isBillingEnabled: false,
            isPickup: 0,
            isDelivery: 0,
            from: null as number | null,
            to: null as number | null,
            additional_services: [] as number[],
            created_at: null as string | null,
            company_id: null as number | null,
            statusIds: [] as number[],
            additionalServiceStatusId: null as number | null,
            takeDateFrom: null as string | null,
            takeDateTo: null as string | null,
            deliveredAtFrom: null as string | null,
            deliveredAtTo: null as string | null,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            cities: new DataState<CityT>({}),
            additional_services: new DataState<AdditionalServicesTypeT>({}),
        })

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true
                const response = await companies.getList({
                    page,
                    search,
                    isExtended: 0,
                    statusId: 2,
                })
                data_state.companies.list = response.data

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(page, search)
                data_state.cities.list = response.data

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadAdditionalServices = async () => {
            data_state.additional_services.pagination.current_page = 1

            try {
                data_state.additional_services.is_loading = true

                const response = await additionalServices.getList()
                data_state.additional_services.list = response.data

                data_state.additional_services.pagination.total_pages = 1

                data_state.additional_services.is_failed = false
                data_state.additional_services.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.additional_services.failure.catchError(error)

                if (!data_state.additional_services.failure.error_state.is_cancel) {
                    data_state.additional_services.is_failed = true
                }
            } finally {
                if (!data_state.additional_services.failure.error_state.is_cancel) {
                    data_state.additional_services.is_loading = false
                }
            }
        }

        const emitApply = () => {
            if (props.loading) return
            emit('apply', { ...state })
        }

        const emitReset = () => {
            if (props.loading) return

            state.additional_services = []
            state.company_id = null
            state.statusIds = []
            state.additionalServiceStatusId = null
            state.created_at = null
            state.from = null
            state.invoice_number = null
            state.isBillingEnabled = false
            state.isPickup = 0
            state.isDelivery = 0
            state.order_id = null
            state.to = null
            state.takeDateFrom = null
            state.takeDateTo = null
            state.deliveredAtFrom = null
            state.deliveredAtTo = null

            emit('reset')
        }

        onBeforeMount(() => {
            loadCompanies(1)
            loadCities(1)
            loadAdditionalServices()
        })

        return {
            state,
            data_state,
            emitApply,
            emitReset,
            loadCompanies,
            loadCities,
            loadAdditionalServices,
            // CONST
            WITH_SERVICES_STATUSES,
            WITH_SERVICES_STATUSES_ADDITIONAL,
        }
    },
})
